import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import i18n from 'src/lib/i18n/i18n';
import StateObserver from 'src/StateObserver';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import * as Admin from 'src/replicant/admin';
import PopupBasic from '../components/popups/PopupBasic';
import * as Pet from 'src/replicant/pet';

import { AssetGroup } from '@play-co/timestep-core';

const skin = {
  opts: {
    width: 591,
    height: 560,
  },
  title: {},
  message: {
    x: 10,
    y: 100,
    height: 100,
    size: 30,
    color: '#9A7171',
    font: bitmapFonts('Body'),
    align: 'center' as const,
    verticalAlign: 'center' as const,
    wrappedWidth: 20,
  },
  previousName: {
    y: 234,
  },
  newName: {
    y: 334,
  },
  button: {
    ...uiConfig.buttons.secondary,
    y: 477,
    width: 259,
    height: 80,
    centerOnOrigin: true,
    font: bitmapFonts('Title'),
    fontSize: 31,
    labelOffsetY: -1,
  },
  barDefaults: {
    frame: {
      x: 90,
      height: 62,
      wrappedWidth: 160,
    },
    caption: {
      y: 28,
      height: 33,
      centerOnOrigin: true,
      font: bitmapFonts('Title'),
      size: 36,
      color: 'white',
      align: 'center' as const,
      verticalAlign: 'center' as const,
      wordWrap: false,
      wrappedWidth: 50,
    },
  },
};

function createBar(opts: { superview: View; y: number; label: string }) {
  const frame = new ImageScaleView({
    ...uiConfig.hud.coinFrame,
    ...skin.barDefaults.frame,
    superview: opts.superview,
    y: opts.y,
    width: opts.superview.style.width - skin.barDefaults.frame.wrappedWidth,
  });

  const caption = new LangBitmapFontTextView({
    superview: frame,
    ...skin.barDefaults.caption,
    x: frame.style.width / 2,
    width: frame.style.width - skin.barDefaults.caption.wrappedWidth,
  });

  return {
    setValue: (string: string) => {
      caption.localeText = () => `${opts.label} ${string}`;
    },
  };
}

export default class PopupAdminReimburse extends PopupBasic {
  private previousName: ReturnType<typeof createBar>;
  private newName: ReturnType<typeof createBar>;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      ...opts,
      ...skin.opts,
      darkerBg: false,
      tracking: { feature: 'admin', $subFeature: 'popup-admin-rename' },
    });

    this.buttonClose.hide();
    this.title.updateOpts(skin.title);

    new LangBitmapFontTextView({
      superview: this.box,
      ...skin.message,
      width: this.box.style.width - skin.message.wrappedWidth,
      localeText: () => i18n('admin.rename.message'),
    });

    this.previousName = createBar({
      superview: this.box,
      ...skin.previousName,
      label: i18n('admin.rename.before'),
    });

    this.newName = createBar({
      superview: this.box,
      ...skin.newName,
      label: i18n('admin.rename.after'),
    });

    new ButtonScaleViewWithText({
      superview: this.box,
      ...skin.button,
      x: this.box.style.width / 2,
      localeText: () => i18n('basic.okay'),
      onClick: async () => opts.close(),
      tracking: { feature: 'admin', $subFeature: 'popup-admin-rename-ok' },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public init(reimbursements: { newPetName: string; previousPetName: string }) {
    super.init({
      title: i18n('admin.rename.title'),
    });

    this.previousName.setValue(reimbursements.previousPetName);
    this.newName.setValue(reimbursements.newPetName);
  }
}
